/*
 * Created by Joseph Saliba on 11/5/16.
 */
import { IConfigService } from '../config/ConfigService';

import IHttpService = angular.IHttpService;
import ITimeoutService = angular.ITimeoutService;
import IHttpPromise = angular.IHttpPromise;
import IPromise = angular.IPromise;

export class TokenService implements IService {
    private cookieName = 'iio_token';
    private http: IHttpService;
    private configService: IConfigService;
    private rootScope: IRootScope;
    private cookies: angular.cookies.ICookiesService;
    private timeout: ITimeoutService;
    private refreshTimer: IPromise<any>;
    private store: any;

    constructor(rootScope: IRootScope, cookies: angular.cookies.ICookiesService, http: IHttpService, timeout: ITimeoutService, configService: app.services.IConfigService) {
      this.rootScope = rootScope;
      this.cookies = cookies;
      this.timeout = timeout;
      this.http = http;
      this.configService = configService;

      this.store = {
        token: cookies.getObject(this.cookieName)
      };

      rootScope.refreshingToken = false;

      if (this.store.token && this.store.token.expires_at) {
        this.scheduleRefreshForExistingToken(this.store.token);
      }

      // Listen for login required, and clear out the token if that's the case
      rootScope.$on("event:auth-loginRequired", () => this.Clear());
    }

    public Clear() {
      console.log('Clearing token');
      this.SetToken(null);
      this.cookies.remove(this.cookieName);
      this.timeout.cancel(this.refreshTimer);
    }

    public Fetch(email: string, password: string, resetPasswordToken = "", idToken = null): IHttpPromise<any> {
      let data = {
        grant_type: idToken ? "sso_oidc_token" : "password",
        username: email,
        password: password,
        resetPasswordToken: resetPasswordToken,
        id_token: idToken
      };

      let paramData = $.param(data);

      const req = this.http({
        method: 'POST',
        url: this.configService.getServerBaseUrl() + '/oauth/token',
        data: paramData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': this.getBasicAuthHeader()
        }
      });

      // Side-effect handlers: both branches covered so derived promise never rejects unhandled.
      // MFARequiredException is an expected mid-login challenge; caller handles it.
      req.then(
        (response: any) => { if (response.data?.access_token) this.SetToken(response.data); },
        (response: any) => { if (response.data?.error !== 'MFARequiredException') { console.log('Token denied'); this.rootScope.$broadcast('token:denied'); } }
      );

      return req; // shimmed promise — caller's .success()/.error() still work
    }

    public CheckStatus(email, password, token) {
      let data = {
        username: email,
        password: password
      };

      let paramData = $.param(data);

      return this.http({
        method: 'POST',
        url: this.configService.getServerBaseUrl() + '/oauth/check_token?token=' + token,
        data: paramData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': this.getBasicAuthHeader()
        }
      }).error(() => {
        console.log('Token denied');
        this.rootScope.$broadcast('token:denied');
      });
    }

    public VerifyMFA(email, password, token, code) {
      let data = {
        grant_type: "mfa",
        MFACode: code,
        MFAToken: token,
        username: email,
        password: password
      };

      let paramData = $.param(data);

      return this.http({
        method: 'POST',
        url: this.configService.getServerBaseUrl() + '/oauth/token',
        data: paramData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': this.getBasicAuthHeader()
        }
      }).success((data: any) => {
        if (data && data.access_token) {
          this.SetToken(data);
        }
      }).error(e => {
        console.log('Token denied');
        this.rootScope.$broadcast('token:denied');
      });
    }

    public Get() {
      this.syncFromCookie();
      return this.store.token;
    }

    public Set(token) {
      this.SetToken(token);
    }

    public GetAccessToken() {
      return (this.Exists() ? this.store.token.access_token : null);
    }

    public Exists() {
      this.syncFromCookie();
      return !_.isUndefined(this.store.token) && !_.isEmpty(this.store.token);

    }

    public Refresh(): IHttpPromise<any> {
      this.syncFromCookie();
      this.rootScope.refreshingToken = true;
      let data = {
        grant_type: "refresh_token",
        refresh_token: (this.store.token && this.store.token.refresh_token) ? this.store.token.refresh_token : ""
      };

      let paramData = $.param(data);

      return this.http({
        method: 'POST',
        url: this.configService.getServerBaseUrl() + '/oauth/token',
        data: paramData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': this.getBasicAuthHeader()
        }
      }).success((data: any) => {
        this.rootScope.refreshingToken = false;
        if (data && data.access_token) {
          this.SetToken(data);
        }
      }).error(() => {
        this.rootScope.refreshingToken = false;
        console.log('Refresh Token denied');
        this.rootScope.$broadcast('token:refresh_denied');
      });
    }

    private getBasicAuthHeader(): string {
      return 'Basic ' + btoa(this.configService.getClientId() + ':' + this.configService.getClientSecret());
    }

    // Another tab may have refreshed (and rotated) the token; adopt the newer cookie token.
    private syncFromCookie() {
      const cookieToken: any = this.cookies.getObject(this.cookieName);
      const current = this.store.token;
      if (!cookieToken?.access_token || cookieToken.access_token === current?.access_token) return;
      if ((cookieToken.expires_at ?? 0) <= (current?.expires_at ?? 0)) return;
      this.store.token = cookieToken;
      this.scheduleRefreshForExistingToken(cookieToken);
    }

    private IsValid() {
      if (!this.Exists()) return false;
      const now = Math.floor(Date.now() / 1000);
      return now <= this.store.token.expires_at;
    }

    private SetToken(token) {
      console.log('Setting token to: ', token);

      // First, cancel any previously scheduled refresh
      if (this.refreshTimer) {
        this.timeout.cancel(this.refreshTimer);
      }

      if (token && token.expires_in) {
        // Calculate the absolute expiration time and store it on the token object
        const nowInSeconds = Math.floor(new Date().getTime() / 1000);
        token.expires_at = nowInSeconds + token.expires_in;

        // Set the cookie with a proper expiration
        const expirationDate = new Date(token.expires_at * 1000);
        this.cookies.putObject(this.cookieName, token, {'expires': expirationDate});

        this.scheduleRefreshForExistingToken(token);

      } else if (token && token.expires_at) {
        // If caller set a token that already has expires_at (edge case), still schedule
        this.scheduleRefreshForExistingToken(token);
      } else {
        // If the token is null, clear the cookie
        this.cookies.remove(this.cookieName);
      }

      this.store.token = token;
    }

    private scheduleRefreshForExistingToken(token: any) {
      if (!token || !token.expires_at) return;

      // Cancel any old timer
      if (this.refreshTimer) {
        this.timeout.cancel(this.refreshTimer);
      }

      const nowSec = Math.floor(Date.now() / 1000);
      const bufferSec = 30;
      const remainingSec = token.expires_at - nowSec;

      // If we’re already within the buffer, refresh immediately
      if (remainingSec <= bufferSec) {
        console.log('Token within buffer after reload; refreshing now.');
        this.Refresh();
        return;
      }

      const refreshDelayMs = (remainingSec - bufferSec) * 1000;
      console.log(`Scheduling token refresh in ${Math.floor(refreshDelayMs / 1000)} seconds (post-reload).`);
      this.refreshTimer = this.timeout(() => this.Refresh(), refreshDelayMs);
    }
}

frontendApp.factory("TokenService", ["$rootScope", "$cookies", "$http", "$timeout", "ConfigService", function($rootScope, $cookies, $http, $timeout, configService) {
  return new TokenService($rootScope, $cookies, $http, $timeout, configService);
}]);
